import React from 'react';

import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
} from '@mui/material';

import { getCancellationPolicyLabel } from '~/utils/cancellationPolicy';
import { FLASH_CANCELLATION_POLICY_VALUES } from '~/utils/mapSFCancelltionPolicyToAdmin';

import { FLASH_RATE_PLAN_PRODUCT_TYPE, getRatePlanEnumOptions } from './utils';
import type { FlashRatePlanDraft, RatePlanCreateSchema } from './utils';

interface Props {
  disabled: boolean;
  draft: FlashRatePlanDraft;
  onClearError: () => void;
  onUpdateDraft: (updates: Partial<FlashRatePlanDraft>) => void;
  ratePlanCreateSchema: RatePlanCreateSchema | undefined;
}

export default function RatePlanDraftFields({
  disabled,
  draft,
  onClearError,
  onUpdateDraft,
  ratePlanCreateSchema,
}: Props) {
  const cancellationPolicyOptions = FLASH_CANCELLATION_POLICY_VALUES;
  const statusOptions = getRatePlanEnumOptions(ratePlanCreateSchema, 'status', draft.ratePlanStatus);
  const rateTypeOptions = getRatePlanEnumOptions(ratePlanCreateSchema, 'rate_type', draft.ratePlanRateType);

  return (
    <Stack spacing={2}>
      <TextField
        label="Rate Plan Name"
        value={draft.ratePlanName}
        disabled={disabled}
        onChange={(event) => {
          onUpdateDraft({ ratePlanName: event.target.value });
          onClearError();
        }}
        required
        fullWidth
      />
      <FormControl fullWidth required disabled={disabled} error={!disabled && !draft.ratePlanCancellationPolicy}>
        <InputLabel id={`flash-offer-rate-plan-cancellation-policy-label-${draft.draftId}`}>
          Cancellation Policy
        </InputLabel>
        <Select
          label="Cancellation Policy"
          labelId={`flash-offer-rate-plan-cancellation-policy-label-${draft.draftId}`}
          value={draft.ratePlanCancellationPolicy}
          onChange={(event) => {
            onUpdateDraft({ ratePlanCancellationPolicy: event.target.value });
            onClearError();
          }}
        >
          {cancellationPolicyOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {getCancellationPolicyLabel(option)}
            </MenuItem>
          ))}
        </Select>
        {!disabled && !draft.ratePlanCancellationPolicy && (
          <FormHelperText>Select a cancellation policy</FormHelperText>
        )}
      </FormControl>
      <FormControl fullWidth required disabled={disabled}>
        <InputLabel id={`flash-offer-rate-plan-status-label-${draft.draftId}`}>Status</InputLabel>
        <Select
          label="Status"
          labelId={`flash-offer-rate-plan-status-label-${draft.draftId}`}
          value={draft.ratePlanStatus}
          onChange={(event) => {
            onUpdateDraft({ ratePlanStatus: event.target.value });
            onClearError();
          }}
        >
          {statusOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField label="Product Type" value={FLASH_RATE_PLAN_PRODUCT_TYPE} required fullWidth disabled />
      <FormControl required disabled={disabled}>
        <FormLabel>Rate Type</FormLabel>
        <RadioGroup
          row
          value={draft.ratePlanRateType}
          onChange={(event) => {
            onUpdateDraft({ ratePlanRateType: event.target.value });
            onClearError();
          }}
        >
          {rateTypeOptions.map((option) => (
            <FormControlLabel key={option} value={option} control={<Radio />} label={option} />
          ))}
        </RadioGroup>
      </FormControl>
    </Stack>
  );
}
